import { useMutation } from "@apollo/client";
import { useEffect } from "react";
import { generateExportFileName } from "../utils/exportFileName";
import { registerPollingConfig } from "./jobPollingConfigs";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { formatDateRangeBoundary } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";
import { addJob as addJobAction, type JobStatus } from "@/store/asyncJobs";
import { useAppDispatch } from "@/store/hooks";

type SupplyChainLotExportStatus = NonNullable<ResultOf<typeof exportStatusQuery>["supplyChainLotExport"]>["status"];

const STATUS_MAP: Record<SupplyChainLotExportStatus, JobStatus> = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  SUCCESS: "COMPLETED",
  FAILED: "ERROR",
};

export const useSupplyChainLotExport = () => {
  const dispatch = useAppDispatch();
  const [createExport, { loading }] = useMutation(createExportMutation);
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { toast } = useToast();

  useEffect(() => {
    registerPollingConfig("SUPPLY_CHAIN_LOT_EXPORT", {
      query: exportStatusQuery,
      getQueryVariables: (job) => ({ export: job.id }),
      getJobStatus: (data) => {
        if (!data.supplyChainLotExport) {
          return {
            status: "ERROR" as const,
            error: "Export not found",
          };
        }

        return {
          status: STATUS_MAP[data.supplyChainLotExport.status] || "ERROR",
          url: data.supplyChainLotExport.url ?? undefined,
          error: data.supplyChainLotExport.status === "FAILED" ? "Export failed" : undefined,
        };
      },
    });
  }, []);

  const handleExport = async (filters?: BenchmarkFilters) => {
    if (!activeBusinessUnitId) {
      toast({
        title: "Export Failed",
        description: "No active business unit found",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data } = await createExport({
        variables: {
          input: {
            businessUnit: activeBusinessUnitId,
            decimalPlaces: 2,
            columns: [
              { column: "REFERENCE", label: "Lot ID" },
              { column: "SORT_NOTE", label: "Sort" },
              { column: "STATUS", label: "Status" },

              // Location and Partner Info
              { column: "SOURCE_PARTNER", label: "Source" },
              { column: "SOURCE_STATE", label: "Source State" },
              { column: "SOURCE_CITY", label: "Source City" },
              { column: "DESTINATION_PARTNER", label: "Destination" },
              { column: "DESTINATION_STATE", label: "Destination State" },
              { column: "DESTINATION_CITY", label: "Destination City" },

              // Timing
              { column: "DATE_OPENED", label: "Opened" },
              { column: "DATE_CLOSED", label: "Closed" },
              { column: "DAYS_ON_FEED", label: "Days on Feed" },

              // Population Statistics
              { column: "NUM_TOTAL_IN", label: "Head In" },
              { column: "NUM_TOTAL_DEAD", label: "Head Dead" },
              { column: "NUM_TOTAL_REMOVED", label: "Head Removed" },
              { column: "NUM_TOTAL_OUT", label: "Head Out" },
              { column: "MORTALITY_RATE", label: "Mortality Rate" },

              // Weight Statistics
              { column: "WEIGHT_AVERAGE_IN", label: "Avg. Weight In" },
              { column: "WEIGHT_AVERAGE_OUT", label: "Avg. Weight Out" },
              { column: "WEIGHT_TOTAL_IN", label: "Total Weight In" },
              { column: "WEIGHT_TOTAL_OUT", label: "Total Weight Out" },
              { column: "WEIGHT_TOTAL_FEED_INTAKE", label: "Total Feed Intake" },

              // Performance Metrics
              { column: "AVERAGE_DAILY_GAIN", label: "ADG" },
              { column: "FEED_CONVERSION", label: "Feed Conversion" },
              { column: "COST_OF_GAIN", label: "Cost of Gain" },

              // Financial Information
              { column: "PURCHASE_PRICE", label: "Purchase Price" },
              { column: "SALE_PRICE", label: "Sale Price" },
              { column: "FEED_COST", label: "Feed Cost" },
              { column: "NON_FEED_COST", label: "Non-Feed Cost" },

              // Grading
              { column: "PRIME_FRACTION", label: "Prime" },
              { column: "CHOICE_FRACTION", label: "Choice" },
              { column: "SELECT_FRACTION", label: "Select" },

              // Animal Characteristics
              { column: "ANIMAL_BREEDS", label: "Breed" },
              { column: "SEX_CLASS", label: "Sex" },

              // Management Info
              { column: "GROUP_BUSINESS_UNIT", label: "Feedyard" },
              { column: "OWNERS", label: "Owners" },
            ],
            ...(filters
              ? {
                  sexClass: filters.sexClass?.length ? filters.sexClass : undefined,
                  animalBreeds: filters.animalBreedIds?.length ? filters.animalBreedIds : undefined,
                  destinationPartners: filters.lotDestinationIds?.length ? filters.lotDestinationIds : undefined,
                  destinationCities: filters.destinationCities?.length ? filters.destinationCities : undefined,
                  destinationStates: filters.destinationStateCodes?.length ? filters.destinationStateCodes : undefined,
                  sourcePartners: filters.lotSourceIds?.length ? filters.lotSourceIds : undefined,
                  sourceStates: filters.sourceStateCodes?.length ? filters.sourceStateCodes : undefined,
                  sourceCities: filters.sourceCities?.length ? filters.sourceCities : undefined,
                  groupBusinessUnits: filters.feedyardIds?.length ? filters.feedyardIds : undefined,

                  // Convert string dates to proper DateTime format for the API
                  dateOpenedAfter: formatDateRangeBoundary.start(filters.dateOpenedAfter),
                  dateOpenedBefore: formatDateRangeBoundary.end(filters.dateOpenedBefore),
                  dateClosedAfter: formatDateRangeBoundary.start(filters.dateClosedAfter),
                  dateClosedBefore: formatDateRangeBoundary.end(filters.dateClosedBefore),

                  // Handle numeric filters consistently
                  weightAverageInMin: filters.weightAverageInMin || undefined,
                  weightAverageInMax: filters.weightAverageInMax || undefined,
                }
              : {}),
          },
        },
      });

      if (!data?.createSupplyChainLotExport?.export) {
        toast({
          title: "Export Failed",
          description: "No export data returned from server",
          variant: "destructive",
        });
        return;
      }

      const exportData = data.createSupplyChainLotExport.export;

      dispatch(
        addJobAction({
          id: exportData.id,
          type: "SUPPLY_CHAIN_LOT_EXPORT",
          status: STATUS_MAP[exportData.status] || "ERROR",
          createdAt: new Date().toISOString(),
          url: exportData.url ?? undefined,
          fileName: generateExportFileName(),
        }),
      );
    } catch (error) {
      toast({
        title: "Export Failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return { handleExport, loading };
};

const exportStatusQuery = graphql(`
  query GetSupplyChainLotExport($export: UUID!) {
    supplyChainLotExport(export: $export) {
      id
      status
      url
    }
  }
`);

const createExportMutation = graphql(`
  mutation CreateSupplyChainLotExport($input: CreateSupplyChainLotExportInput!) {
    createSupplyChainLotExport(input: $input) {
      export {
        id
        status
        url
      }
    }
  }
`);
